import React, { useState } from 'react'
import { Box, Button, Tab, Tabs, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import useRouter from '../../hooks/useRouter'
import DashboardLayoutComponent from '../dashboard/DashboardLayoutComponent'
import type { DashboardConfig } from '../../types/gotypes'
import { CARD_RADIUS, HAIRLINE, PILL_RADIUS } from '../../agent'

interface InlineDashboardProps {
  dashboardId: string
  customer: string
  config: DashboardConfig
  viewUrl: string
}

export default function InlineDashboard({ dashboardId, customer, config, viewUrl }: InlineDashboardProps) {
  const router = useRouter()
  const pages = config.pages || []
  const [activePageIndex, setActivePageIndex] = useState(0)

  // Use page layout if multi-page, otherwise fall back to top-level layout
  const activeLayout = pages.length > 0
    ? pages[activePageIndex]?.layout
    : config.layout

  return (
    <Box sx={{
      mt: 1,
      width: '100%',
      maxWidth: '100%',
      border: HAIRLINE,
      borderRadius: CARD_RADIUS,
      overflow: 'hidden',
      backgroundColor: 'white',
    }}>
      {/* Header */}
      <Box sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: HAIRLINE,
        backgroundColor: '#f9fafb',
      }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{config.name}</Typography>
          {config.description && (
            <Typography sx={{ fontSize: 12, color: '#6b7280' }}>{config.description}</Typography>
          )}
        </Box>
        {/* Same tab, through the router. The login lives in this tab's
            session storage, so a new tab asks the reader to log in again
            (Adam, 11 Sep). href stays for the status bar and middle-click. */}
        <Button
          size="small"
          color="info"
          href={viewUrl}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
            e.preventDefault()
            router.navigate('dashboard-view', { customer, dashboardId })
          }}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none', fontSize: 12, borderRadius: PILL_RADIUS }}
        >
          Open dashboard
        </Button>
      </Box>

      {/* Page tabs */}
      {pages.length > 1 && (
        <Tabs
          value={activePageIndex}
          onChange={(_e, v) => setActivePageIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 36, borderBottom: '1px solid #e5e7eb', px: 1 }}
        >
          {pages.map((page, i) => (
            <Tab key={page.id || i} label={page.name || `Page ${i + 1}`} sx={{ minHeight: 36, textTransform: 'none', fontSize: 13 }} />
          ))}
        </Tabs>
      )}

      {/* Layout */}
      {activeLayout && (
        <Box sx={{ p: 2 }}>
          <DashboardLayoutComponent
            layout={activeLayout}
            customer={customer}
            readOnly
          />
        </Box>
      )}
    </Box>
  )
}
